/**
 * Kudos native ↔ web bridge (web-app side).
 *
 * Shell → web: `InAppBrowser.postMessage` fires `messageFromNative` on window.
 * Web → shell: `window.mobileApp.postMessage({ detail })`.
 *
 * File organization:
 *   §1 Protocol constants and shared types
 *   §2 Core transport helpers (send, build, request/response correlation)
 *   §3 Type guards
 *   §4 Device slice (types, parser, outbound request) — UNUSED in production today
 *   §5 Push slice (types, parsers, outbound requests)
 *   §6 Sample slice (types, parser, outbound request) — UNUSED in production today
 *   §7 Router and listener registration
 */

// ---------------------------------------------------------------------------
// §1 Protocol constants and shared types
// ---------------------------------------------------------------------------

export const KUDOS_NATIVE_BRIDGE_PROTOCOL_VERSION = 1
export const KUDOS_NATIVE_BRIDGE_CHANNEL = 'kudos-native-bridge'

const BRIDGE_RESPONSE_TIMEOUT_MS = 30_000

export type KudosNativeBridgeAction =
  | 'device.getInfo'
  | 'push.setToken'
  | 'push.notificationTap'
  | 'push.requestToken'
  | 'push.checkPermission'
  | 'push.requestPermission'
  | 'sample.ping'
  | 'sample.notify'

type BridgeMessageEnvelope = {
  protocolVersion: typeof KUDOS_NATIVE_BRIDGE_PROTOCOL_VERSION
  channel: typeof KUDOS_NATIVE_BRIDGE_CHANNEL
  action: KudosNativeBridgeAction
  requestId?: string
  payload?: Record<string, unknown>
}

type BridgeResponse = {
  protocolVersion: typeof KUDOS_NATIVE_BRIDGE_PROTOCOL_VERSION
  channel: typeof KUDOS_NATIVE_BRIDGE_CHANNEL
  kind: 'ack' | 'error'
  requestId?: string
  action?: KudosNativeBridgeAction
  ok?: boolean
  message?: string
  code?: string
  data?: Record<string, unknown>
}

type MobileAppBridge = {
  postMessage: (msg: { detail: Record<string, unknown> }) => void
}

// ---------------------------------------------------------------------------
// §2 Core transport helpers
// ---------------------------------------------------------------------------

/**
 * Returns the native shell bridge object when the app is running inside the
 * Capacitor WebView, otherwise undefined.
 *
 * The shell injects `window.mobileApp` at WebView startup; there is no way to
 * extend the global Window type for this third-party injection, so the cast is
 * necessary and intentional (third-party library boundary).
 *
 * @returns The `window.mobileApp` bridge object, or undefined.
 */
const getMobileApp = (): MobileAppBridge | undefined =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).mobileApp as MobileAppBridge | undefined

/**
 * Returns true when the web app is running inside the Capacitor shell WebView.
 * @returns True if `window.mobileApp.postMessage` is available.
 */
export const isKudosMobileShell = (): boolean => typeof getMobileApp()?.postMessage === 'function'

/**
 * Generates a unique correlation ID for bridge request/response pairs.
 * Falls back to a timestamp-based ID when `crypto.randomUUID` is unavailable.
 *
 * @returns A unique string request ID.
 */
const randomRequestId = (): string => {
  try {
    return globalThis.crypto?.randomUUID?.() ?? `req-${Date.now()}`
  } catch {
    return `req-${Date.now()}`
  }
}

/**
 * Returns true if the raw message object carries the correct bridge channel and
 * protocol version fields.
 *
 * @param d - A raw key-value record from a bridge event.
 * @returns True when channel and protocolVersion match the expected constants.
 */
const hasBridgeChannelAndVersion = (d: Record<string, unknown>): boolean =>
  d['channel'] === KUDOS_NATIVE_BRIDGE_CHANNEL &&
  d['protocolVersion'] === KUDOS_NATIVE_BRIDGE_PROTOCOL_VERSION

/**
 * Builds a typed bridge message envelope for an outbound web → shell request.
 *
 * @param action - The bridge action to perform.
 * @param options - Optional requestId (for correlated calls) and payload.
 * @returns A typed BridgeMessageEnvelope ready to send.
 */
const buildBridgeMessage = (
  action: KudosNativeBridgeAction,
  options?: { requestId?: string; payload?: Record<string, unknown> },
): BridgeMessageEnvelope => ({
  protocolVersion: KUDOS_NATIVE_BRIDGE_PROTOCOL_VERSION,
  channel: KUDOS_NATIVE_BRIDGE_CHANNEL,
  action,
  requestId: options?.requestId,
  payload: options?.payload,
})

/**
 * Builds a typed bridge response envelope for correlated ack/error replies.
 *
 * @param kind - 'ack' for success, 'error' for failure.
 * @param options - Optional requestId, action, ok flag, data payload, error code, and message.
 * @returns A typed BridgeResponse envelope.
 */
const buildBridgeResponse = (
  kind: 'ack' | 'error',
  options: {
    requestId?: string
    action?: KudosNativeBridgeAction
    ok?: boolean
    data?: Record<string, unknown>
    code?: string
    message?: string
  },
): BridgeResponse => ({
  protocolVersion: KUDOS_NATIVE_BRIDGE_PROTOCOL_VERSION,
  channel: KUDOS_NATIVE_BRIDGE_CHANNEL,
  kind,
  requestId: options.requestId,
  action: options.action,
  ok: options.ok,
  data: options.data,
  code: options.code,
  message: options.message,
})

/**
 * Sends any bridge message or response to the native shell via
 * `window.mobileApp.postMessage`. No-op when the shell is not present.
 *
 * @param detail - The envelope or response to send.
 */
export const sendMessageToShell = (detail: BridgeMessageEnvelope | BridgeResponse): void => {
  const mobileApp = getMobileApp()
  if (!mobileApp) return
  mobileApp.postMessage({ detail: { ...detail } })
}

// UNUSED: sendResponseToShell is not called in production today.
// Kept for the shell api.invoke handshake pattern (web acks a shell-initiated request).
/**
 * Sends a correlated ack/error reply from web to shell.
 *
 * @param response - The bridge response envelope to send.
 */
export const sendResponseToShell = (response: BridgeResponse): void => {
  sendMessageToShell(response)
}

// ---------------------------------------------------------------------------
// Request/response correlation state
// ---------------------------------------------------------------------------

interface PendingWebRequest {
  resolve: (data: Record<string, unknown>) => void
  reject: (err: Error) => void
  timer: ReturnType<typeof setTimeout>
}

const pendingFromShell = new Map<string, PendingWebRequest>()

/**
 * Settles a pending correlated request when the shell sends its matching response.
 * Resolves on 'ack', rejects on 'error'. Clears the timeout timer.
 *
 * @param requestId - The correlation ID that was included in the original request.
 * @param response - The BridgeResponse received from the shell.
 */
const settlePendingFromShell = (requestId: string, response: BridgeResponse): void => {
  const pending = pendingFromShell.get(requestId)
  if (!pending) return
  clearTimeout(pending.timer)
  pendingFromShell.delete(requestId)
  if (response.kind === 'error') {
    pending.reject(new Error(response.message ?? response.code ?? 'Bridge error'))
    return
  }
  pending.resolve(response.data ?? {})
}

/**
 * Returns a promise that resolves when the shell sends a correlated ack containing
 * the given requestId, or rejects after BRIDGE_RESPONSE_TIMEOUT_MS milliseconds.
 *
 * @param requestId - The correlation ID to wait for.
 * @returns A promise that resolves with the shell response data payload.
 */
const waitForShellResponse = <T>(requestId: string): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      pendingFromShell.delete(requestId)
      reject(new Error('Shell did not respond in time'))
    }, BRIDGE_RESPONSE_TIMEOUT_MS)

    pendingFromShell.set(requestId, {
      resolve: data => resolve(data as T),
      reject,
      timer,
    })
  })

// ---------------------------------------------------------------------------
// §3 Type guards
// ---------------------------------------------------------------------------

/**
 * Returns true if `detail` is a well-formed inbound bridge message from the shell
 * (i.e. an action message, not a correlated response).
 *
 * @param detail - Unknown value extracted from a messageFromNative event.
 * @returns True when the detail is a valid BridgeMessageEnvelope.
 */
const isNativeBridgeMessage = (detail: unknown): detail is BridgeMessageEnvelope => {
  if (!detail || typeof detail !== 'object') return false
  const d = detail as Record<string, unknown>
  return hasBridgeChannelAndVersion(d) && typeof d['action'] === 'string' && d['kind'] === undefined
}

/**
 * Returns true if `detail` is a well-formed correlated response from the shell
 * (i.e. an ack or error answering a web-initiated request).
 *
 * @param detail - Unknown value extracted from a messageFromNative event.
 * @returns True when the detail is a valid BridgeResponse.
 */
const isShellBridgeResponse = (detail: unknown): detail is BridgeResponse => {
  if (!detail || typeof detail !== 'object') return false
  const d = detail as Record<string, unknown>
  return hasBridgeChannelAndVersion(d) && (d['kind'] === 'ack' || d['kind'] === 'error')
}

// ---------------------------------------------------------------------------
// §4 Device slice — UNUSED in production today
// ---------------------------------------------------------------------------

// UNUSED: device.getInfo, requestDeviceInfoFromShell, and parseDeviceInfoPayload
// are not called in any production path today.
// Keep until device-info display or analytics is implemented.

export interface DeviceInfoPayload extends Record<string, unknown> {
  platform: string
  deviceType: 'mobile' | 'desktop' | 'unknown'
  isNative: boolean
  operatingSystem?: string
}

/**
 * Parses a raw shell response into a DeviceInfoPayload.
 *
 * @param data - Raw key-value data from the shell's device.getInfo ack.
 * @returns A typed DeviceInfoPayload, or null when required fields are missing or wrong type.
 */
const parseDeviceInfoPayload = (data: Record<string, unknown>): DeviceInfoPayload | null => {
  const platform = data['platform']
  const deviceType = data['deviceType']
  const isNative = data['isNative']
  if (typeof platform !== 'string' || typeof isNative !== 'boolean') return null
  if (deviceType !== 'mobile' && deviceType !== 'desktop' && deviceType !== 'unknown') return null
  const operatingSystem =
    typeof data['operatingSystem'] === 'string' ? data['operatingSystem'] : undefined
  return { platform, deviceType, isNative, operatingSystem }
}

// UNUSED: not called in production today.
/**
 * Web → shell: requests device and runtime information.
 *
 * @returns A promise resolving with the parsed device info payload.
 * @throws When the shell returns an invalid or malformed response.
 */
export const requestDeviceInfoFromShell = async (): Promise<DeviceInfoPayload> => {
  const requestId = randomRequestId()
  const responsePromise = waitForShellResponse<Record<string, unknown>>(requestId)
  sendMessageToShell(buildBridgeMessage('device.getInfo', { requestId }))
  const data = await responsePromise
  const device = parseDeviceInfoPayload(data)
  if (!device) throw new Error('Invalid device.getInfo response from shell')
  return device
}

// ---------------------------------------------------------------------------
// §5 Push slice
// ---------------------------------------------------------------------------

export interface PushTokenPayload extends Record<string, unknown> {
  token: string
  platform?: string
}

export interface PushNotificationTapPayload extends Record<string, unknown> {
  /**
   * For type 'client': a frontend path, e.g. "/recognitions/123". Navigate inside the SPA.
   * For type 'api':    a full URL, e.g. "https://k-api.kudos.com/...". Open in browser.
   */
  path: string
  /**
   * 'client' — path is a frontend route; navigate() inside the SPA (default, 99% of cases).
   * 'api'    — path is a full API URL; open externally (server redirects to a frontend route).
   */
  type?: 'client' | 'api'
  /** Raw notification data forwarded from the OS payload. */
  data?: Record<string, unknown>
}

/**
 * Response from push.checkPermission and push.requestPermission.
 * enabled=true means OS push permission is granted and the token is valid.
 * enabled=false means push is disabled in device settings — show the toggle as OFF.
 */
export interface PushCheckPermissionPayload {
  enabled: boolean
  token?: string
  platform?: string
}

/**
 * Parses a raw bridge payload into a PushTokenPayload.
 *
 * @param payload - Raw payload from an inbound push.setToken message.
 * @returns A typed PushTokenPayload, or null when required fields are missing or wrong type.
 */
const parsePushTokenPayload = (
  payload: Record<string, unknown> | undefined,
): PushTokenPayload | null => {
  if (!payload || typeof payload['token'] !== 'string') return null
  const platform = payload['platform']
  return {
    token: payload['token'],
    platform: typeof platform === 'string' ? platform : undefined,
  }
}

/**
 * Parses a raw bridge payload into a PushNotificationTapPayload.
 *
 * @param payload - Raw payload from an inbound push.notificationTap message.
 * @returns A typed PushNotificationTapPayload, or null when required fields are missing or wrong type.
 */
const parsePushNotificationTapPayload = (
  payload: Record<string, unknown> | undefined,
): PushNotificationTapPayload | null => {
  if (!payload || typeof payload['path'] !== 'string') return null
  const rawType = payload['type']
  const type: 'client' | 'api' = rawType === 'api' ? 'api' : 'client'
  const data =
    payload['data'] && typeof payload['data'] === 'object'
      ? (payload['data'] as Record<string, unknown>)
      : undefined
  return { path: payload['path'], type, data }
}

/**
 * Web → shell: checks whether the device has push notifications enabled in OS settings.
 *
 * Used on component mount to sync the toggle with the real device state without showing
 * a permission dialog. If enabled, the shell also returns the current token so the
 * component can detect rotation and re-register if needed.
 *
 * @returns A promise resolving with the current push permission and token state.
 */
export const checkPushPermissionFromShell = async (): Promise<PushCheckPermissionPayload> => {
  const requestId = randomRequestId()
  const responsePromise = waitForShellResponse<Record<string, unknown>>(requestId)
  sendMessageToShell(buildBridgeMessage('push.checkPermission', { requestId }))
  const data = await responsePromise
  return {
    enabled: data['enabled'] === true,
    token: typeof data['token'] === 'string' ? data['token'] : undefined,
    platform: typeof data['platform'] === 'string' ? data['platform'] : undefined,
  }
}

/**
 * Web → shell: requests push notification permission from the OS (may show system dialog).
 *
 * Unlike checkPushPermissionFromShell, this call may trigger the OS permission prompt
 * when the user has not yet been asked. The shell calls PushNotifications.requestPermissions()
 * before checking device-level state and registering for a token.
 *
 * @returns A promise resolving with the resulting push permission and token state.
 */
export const requestPushPermissionFromShell = async (): Promise<PushCheckPermissionPayload> => {
  const requestId = randomRequestId()
  const responsePromise = waitForShellResponse<Record<string, unknown>>(requestId)
  sendMessageToShell(buildBridgeMessage('push.requestPermission', { requestId }))
  const data = await responsePromise
  return {
    enabled: data['enabled'] === true,
    token: typeof data['token'] === 'string' ? data['token'] : undefined,
    platform: typeof data['platform'] === 'string' ? data['platform'] : undefined,
  }
}

// UNUSED: requestPushTokenFromShell (push.requestToken) is not called in production today.
// Kept as an alternative token-request path for future use.
/**
 * Web → shell: requests an FCM/APNs device token on demand without a permission dialog.
 *
 * Asks the shell to call PushNotifications.register() and returns the token once the OS
 * responds. Use requestPushPermissionFromShell when an OS dialog may be needed.
 *
 * @returns A promise resolving with the push token payload.
 * @throws When the shell responds without a valid token string.
 */
export const requestPushTokenFromShell = async (): Promise<PushTokenPayload> => {
  const requestId = randomRequestId()
  const responsePromise = waitForShellResponse<Record<string, unknown>>(requestId)
  sendMessageToShell(buildBridgeMessage('push.requestToken', { requestId }))
  const data = await responsePromise
  if (typeof data['token'] !== 'string') {
    throw new Error('Shell did not return a valid push token')
  }
  return {
    token: data['token'],
    platform: typeof data['platform'] === 'string' ? data['platform'] : undefined,
  }
}

// ---------------------------------------------------------------------------
// §6 Sample slice — UNUSED in production today
// ---------------------------------------------------------------------------

// UNUSED: sample.ping, sample.notify, parseSampleNotifyPayload, and sendSamplePingToShell
// are not called in any production path today.
// Keep as reference implementations for the request/response and fire-and-forget patterns.

export interface SampleNotifyPayload extends Record<string, unknown> {
  message: string
}

/**
 * Parses a raw bridge payload into a SampleNotifyPayload.
 *
 * @param payload - Raw payload from an inbound sample.notify message.
 * @returns A typed SampleNotifyPayload, or null when required fields are missing or wrong type.
 */
const parseSampleNotifyPayload = (
  payload: Record<string, unknown> | undefined,
): SampleNotifyPayload | null => {
  if (!payload || typeof payload['message'] !== 'string') return null
  return { message: payload['message'] }
}

// UNUSED: not called in production today.
/**
 * Web → shell: sends a sample echo ping and waits for the shell to echo it back.
 *
 * @param message - The message string to echo. Defaults to 'ping'.
 * @returns A promise resolving with the shell's raw echo response data.
 */
export const sendSamplePingToShell = async (message = 'ping'): Promise<Record<string, unknown>> => {
  const requestId = randomRequestId()
  const responsePromise = waitForShellResponse<Record<string, unknown>>(requestId)
  sendMessageToShell(buildBridgeMessage('sample.ping', { requestId, payload: { message } }))
  return responsePromise
}

// ---------------------------------------------------------------------------
// §7 Router and listener registration
// ---------------------------------------------------------------------------

export interface KudosNativeBridgeHandlers {
  /** Shell → web: FCM/APNs token is ready. Register it with the backend. */
  onPushToken?(payload: PushTokenPayload): void
  /** Shell → web: user tapped a push notification. Navigate to the given path. */
  onPushNotificationTap?(payload: PushNotificationTapPayload): void
  // UNUSED: onSampleNotify is not used in production today.
  /** Sample: shell → web one-way event. */
  onSampleNotify?(payload: SampleNotifyPayload): void
}

/**
 * Dispatches an inbound bridge message or shell response to the appropriate handler.
 *
 * Correlated responses (ack/error) are routed to settlePendingFromShell.
 * Inbound action messages are routed to the matching handler by action name.
 * Unknown or malformed messages are silently ignored.
 *
 * @param detail - The raw event detail from a messageFromNative CustomEvent.
 * @param handlers - The set of registered bridge event handlers.
 */
const dispatchToHandlers = (detail: unknown, handlers: KudosNativeBridgeHandlers): void => {
  // Correlated shell response (shell answered a web-initiated request)
  if (isShellBridgeResponse(detail)) {
    if (detail.requestId) {
      settlePendingFromShell(detail.requestId, detail)
    }
    return
  }

  if (!isNativeBridgeMessage(detail)) return

  switch (detail.action) {
    case 'push.setToken': {
      const pushPayload = parsePushTokenPayload(detail.payload)
      if (pushPayload) handlers.onPushToken?.(pushPayload)
      break
    }
    case 'push.notificationTap': {
      const tapPayload = parsePushNotificationTapPayload(detail.payload)
      if (tapPayload) handlers.onPushNotificationTap?.(tapPayload)
      break
    }
    case 'sample.notify': {
      const notifyPayload = parseSampleNotifyPayload(detail.payload)
      if (notifyPayload) handlers.onSampleNotify?.(notifyPayload)
      break
    }
    // Shell answers web-initiated requests — handled above via isShellBridgeResponse.
    case 'device.getInfo':
    case 'push.requestToken':
    case 'sample.ping':
      break
    default:
      break
  }
}

let registered = false

/**
 * Events that arrive via `messageFromNative` before `registerKudosNativeBridgeListener`
 * is called are buffered here and replayed once a handler is registered.
 *
 * Why this matters: the shell calls `evaluateJavascript` (which fires `messageFromNative`)
 * right after `openWebView` resolves. At that point the SPA's module code has run, but
 * React's `useEffect` may not have executed yet, so the listener registered there hasn't
 * been added. Without buffering the event lands on nothing and the notification tap is lost.
 */
const pendingNativeEvents: CustomEvent<unknown>[] = []

/**
 * Module-level buffering listener. Installed at import time (before any React effects run)
 * so we capture events that arrive during the React render → commit → effect window.
 * Stored in a named variable so it can be cleanly removed if needed.
 *
 * @param evt - The raw DOM Event from the messageFromNative listener.
 */
const onBufferingMessage = (evt: Event): void => {
  if (!registered) {
    pendingNativeEvents.push(evt as CustomEvent<unknown>)
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('messageFromNative', onBufferingMessage)
}

/**
 * Stored reference to the currently registered handler listener function.
 * null when no handler is registered. Held so unregisterKudosNativeBridgeListener
 * can call removeEventListener with the exact same function reference.
 */
let registeredListenerFn: ((evt: Event) => void) | null = null

/**
 * Registers the global `messageFromNative` listener with the provided handlers.
 *
 * Safe to call multiple times — only registers once per page lifecycle.
 * Replays any buffered events that arrived before this call
 * (i.e. before the React effect had a chance to run).
 *
 * @param handlers - The bridge event handlers to register.
 */
export const registerKudosNativeBridgeListener = (handlers: KudosNativeBridgeHandlers): void => {
  if (registered || typeof window === 'undefined') return
  registered = true

  // Replay events that arrived before this handler was registered.
  for (const evt of pendingNativeEvents.splice(0)) {
    dispatchToHandlers(evt.detail, handlers)
  }

  // Store the listener reference so unregisterKudosNativeBridgeListener can remove it.
  registeredListenerFn = (evt: Event) => {
    dispatchToHandlers((evt as CustomEvent<unknown>).detail, handlers)
  }
  window.addEventListener('messageFromNative', registeredListenerFn)
}

/**
 * Removes the `messageFromNative` handler listener and resets all registration state.
 *
 * Call on logout or hook unmount so a future re-registration (e.g. after a new login
 * session) gets a fresh handler with up-to-date navigate/auth refs.
 * After this call, new events will be buffered again until the next register call.
 */
export const unregisterKudosNativeBridgeListener = (): void => {
  if (typeof window !== 'undefined' && registeredListenerFn) {
    window.removeEventListener('messageFromNative', registeredListenerFn)
    registeredListenerFn = null
  }
  registered = false
}

// UNUSED: buildNativeBridgeResponse is not used in production today.
// Re-exported for the shell api.invoke handshake pattern.
export { buildBridgeResponse as buildNativeBridgeResponse }
